import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import SearchIcon from '@mui/icons-material/Search';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import ThemeButton from 'shared/ui/Button';
import { MainDatagrid } from 'shared/ui/Datagrid';
import CircularLoading from 'shared/ui/CircularLoading';
import { formatDateToDateString } from 'shared/lib/dateFormatter';
import { operationRangeDaySlot } from 'shared/ui/OperationPickersDay';

import { userActivityCols, systemActivityCols } from '../lib/UserActivityDef';
import UserActivityPanel from './UserActivityPanel';

export default function UserActivity(props) {
    /** Get the props from activity.js. */
    const { tabValue } = props;

    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State for user activity row. */
    const [userActivityRows, setUserActivityRows] = useState([]);
    /** State for initial loading. */
    const [initialLoading, setInitialLoading] = useState(true);
    /** State for search date value. */
    const [dateValue, setDateValue] = useState([null, null]);
    /** State for selected row. */
    const [selectedRow, setSelectedRow] = useState(null);

    /** Named website title. */
    useEffect(() => {
        document.title = 'User Activity | Operation System';
    }, []);

    useEffect(() => {
        callAPI({
            method: 'get',
            module: tabValue === 0 ? 'user_activity' : 'system_activity',
            getAccessTokenSilently,
            email: user.email,
            onInit: () => setInitialLoading(true),
            onSuccess: (resp) => {
                const numberedData = resp.data.map((item, index) => ({
                    id: index + 1,
                    ...item,
                }));
                setUserActivityRows(numberedData);
                setInitialLoading(false);
            },
            onFinal: () => setInitialLoading(false),
        });
    }, [user.email]);

    /** Handle to change date value. */
    const handleDateChange = (event) => {
        setDateValue(event);
    };

    /** Handle to submit search values to server. */
    const handleSearch = () => {
        callAPI({
            method: 'post',
            module: tabValue === 0 ? 'user_activity' : 'system_activity',
            getAccessTokenSilently,
            email: user.email,
            // 관리자가 고른 날짜를 그대로 보낸다. 날짜 객체를 그냥 넘기면 시각이 함께
            // 실려, 한국에서 고른 날짜가 서버에서 전날로 읽혀 고른 적 없는 날의 로그가 나온다.
            obj: JSON.stringify(dateValue.map((d) => (d ? formatDateToDateString(d.toDate()) : null))),
            onInit: () => setInitialLoading(true),
            onSuccess: (resp) => {
                const numberedData = resp.data.map((item, index) => ({
                    id: index + 1,
                    ...item,
                }));
                setUserActivityRows(numberedData);
                setInitialLoading(false);
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Something went wrong, please try again.',
                        severity: 'error',
                    })
                ),
            onFinal: () => {
                setInitialLoading(false);
            },
        });
    };

    const handleDetailClick = (id) => {
        const selectedRow = userActivityRows.find((row) => row.id === id);
        setSelectedRow(selectedRow);
    };

    const activityCol = tabValue === 0 ? userActivityCols(handleDetailClick) : systemActivityCols(handleDetailClick);

    return (
        <>
            {initialLoading && <CircularLoading loading={initialLoading} />}
            <Grid container spacing={2}>
                <Grid size={7.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#003a52', display: 'inline', mr: 1 }}>
                            Date Range
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateRangePicker
                                value={dateValue}
                                onChange={handleDateChange}
                                slots={operationRangeDaySlot}
                                slotProps={{ textField: { InputLabelProps: { shrink: true } } }}
                                localeText={{ start: '', end: '' }}
                                sx={{ '& .MuiInputBase-input': { px: 1, py: 0, height: '32px' }, width: '15vw' }}
                            />
                        </LocalizationProvider>
                        <ThemeButton
                            text={'SEARCH'}
                            icon={<SearchIcon />}
                            width={'80px'}
                            sx={{ ml: 2, fontSize: 13 }}
                            onClick={handleSearch}
                        />
                    </Box>
                    <MainDatagrid
                        rows={userActivityRows}
                        cols={activityCol}
                        getRowHeight={() => 'auto'}
                        showToolbar={'GridToolbar'}
                        id_status={false}
                        sx={{ height: '80vh', fontSize: 11 }}
                    />
                </Grid>
                <Grid size={4.5}>
                    <UserActivityPanel row={selectedRow} />
                </Grid>
            </Grid>
        </>
    );
}
